import React, { useState, useEffect } from 'react';
import { useWallet as useSolanaWallet } from '@solana/wallet-adapter-react';
import { WalletMultiButton } from '@solana/wallet-adapter-react-ui';
import { useWallet } from '../../contexts/WalletContext';
import { CopyableAddress } from '../common/CopyButton';

const ExternalWalletConnector = ({ onClose, onSuccess }) => {
  const { 
    wallet: solanaWallet, 
    publicKey, 
    connected, 
    connecting, 
    disconnect 
  } = useSolanaWallet();
  
  const { 
    connectExternalWallet, 
    isLoading, 
    error, 
    clearError 
  } = useWallet();

  const [isConnecting, setIsConnecting] = useState(false);
  const [connectionStep, setConnectionStep] = useState('select'); // 'select', 'connecting', 'saving'

  useEffect(() => {
    if (connected && publicKey && connectionStep === 'connecting') {
      handleSaveWallet();
    }
  }, [connected, publicKey, connectionStep]);

  const handleSaveWallet = async () => {
    if (!publicKey) return;

    setConnectionStep('saving');
    setIsConnecting(true);

    try {
      const result = await connectExternalWallet(publicKey.toString());
      
      if (result.success) {
        onSuccess(result.wallet);
        // Don't disconnect the wallet after successful connection
      } else {
        // If saving failed, disconnect the wallet
        await disconnect();
        setConnectionStep('select');
      }
    } catch (error) {
      console.error('Error saving wallet:', error);
      await disconnect();
      setConnectionStep('select');
    } finally {
      setIsConnecting(false);
    }
  };

  const handleWalletConnect = () => {
    setConnectionStep('connecting');
    clearError();
  };

  const handleCancel = async () => {
    if (connected) {
      await disconnect();
    }
    setConnectionStep('select');
    onClose();
  };

  const getStepMessage = () => {
    switch (connectionStep) {
      case 'select':
        return 'Select and connect your wallet';
      case 'connecting':
        return 'Connecting to your wallet...';
      case 'saving':
        return 'Saving wallet to your account...';
      default:
        return '';
    }
  };

  const getWalletIcon = (walletName) => {
    const icons = {
      'Phantom': '👻',
      'Solflare': '🔥',
      'Coinbase Wallet': '🔵',
      'Trust Wallet': '🛡️',
      'default': '👛'
    };
    return icons[walletName] || icons.default;
  };

  return (
    <div className="external-wallet-connector">
      <div className="connector-header">
        <h3>Connect External Wallet</h3>
        <button className="close-button" onClick={handleCancel}>
          ×
        </button>
      </div>

      <div className="connector-content">
        {error && (
          <div className="error-message">
            <span className="error-icon">❌</span>
            {error}
            <button className="error-close" onClick={clearError}>×</button>
          </div>
        )}

        <div className="connection-status">
          <div className="status-indicator">
            {connectionStep === 'select' && '1️⃣'}
            {connectionStep === 'connecting' && '2️⃣'}
            {connectionStep === 'saving' && '3️⃣'}
          </div>
          <p className="status-message">{getStepMessage()}</p>
        </div>

        {connected && publicKey && (
          <div className="connected-wallet-info">
            <div className="wallet-details">
              <div className="wallet-icon">
                {getWalletIcon(solanaWallet?.adapter?.name)}
              </div>
              <div className="wallet-info">
                <h4>{solanaWallet?.adapter?.name || 'Unknown Wallet'}</h4>
                <div className="wallet-address">
                  <CopyableAddress 
                    address={publicKey.toString()}
                    truncate={true}
                    truncateLength={8}
                  />
                </div>
              </div>
            </div>
            
            {connectionStep === 'saving' && (
              <div className="saving-indicator">
                <div className="spinner"></div>
                <p>Saving to your account...</p>
              </div>
            )}
          </div>
        )}

        <div className="wallet-adapter-container">
          <WalletMultiButton 
            onClick={handleWalletConnect}
            disabled={isConnecting || isLoading}
          />
        </div>

        {!connected && (
          <div className="wallet-instructions">
            <h4>How to connect:</h4>
            <ol>
              <li>Click "Select Wallet" above</li>
              <li>Choose your preferred wallet from the list</li>
              <li>Approve the connection in your wallet</li>
              <li>Your wallet will be saved to your account</li>
            </ol>
            
            <div className="supported-wallets">
              <h5>Supported Wallets:</h5>
              <div className="wallet-grid">
                <div className="wallet-item">👻 Phantom</div>
                <div className="wallet-item">🔥 Solflare</div>
                <div className="wallet-item">🔵 Coinbase</div>
                <div className="wallet-item">🛡️ Trust Wallet</div>
              </div>
              <p style={{ fontSize: '12px', color: '#6c757d', marginTop: '12px' }}>
                More wallets can be added upon request
              </p>
            </div>
          </div>
        )}

        <div className="connector-actions">
          <button 
            className="cancel-button" 
            onClick={handleCancel}
            disabled={isConnecting || isLoading}
          >
            Cancel
          </button>
          
          {connected && connectionStep !== 'saving' && (
            <button 
              className="save-button" 
              onClick={handleSaveWallet}
              disabled={isConnecting || isLoading}
            >
              Save Wallet
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ExternalWalletConnector;